// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@adl/App/Store/Reducers";
import { VideoDocumentModel } from "@adl/App/Store/Reducers/ProductDetailsReducer";
import { getProductVideos } from "@adl/App/Store/Selectors/ProductDetailsSelector";
import { getSettingsCollection } from "@adl/App/Store/Selectors/SettingsSelectors";
import LazyImage, { LazyImageProps } from "@adl/MobiusOverrides/LazyImage/LazyImage";
import { xScreen } from "@adl/XDL/Utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { ImageModel } from "@insite/client-framework/Types/ApiModels";
import Clickable from "@insite/mobius/Clickable";
import { GridItemProps } from "@insite/mobius/GridItem";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import ChevronUp from "@insite/mobius/Icons/ChevronUp";
import { injectCss } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useEmblaCarousel } from "embla-carousel/react";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

interface OwnProps {
    scrollingBehavior?: string;
    numberOfVisibleThumbnails?: number;
    selectedIndex?: number;
    selectedVideoIndex?: number;
    onSelectImage?: (index: number) => void;
    onSelectVideo?: (index: number) => void;
    extendedStyles?: ProductImageCarouselStyles;
}

const mapStateToProps = (state: CustomAppState, ownProps: HasProductContext) => ({
    productSettings: getSettingsCollection(state).productSettings,
    // XNG-Changes
    customSettings: getSettingsCollection(state).customSettings,
    videos: getProductVideos(state, ownProps.productContext?.product),
});

type Props = OwnProps & HasProductContext & ReturnType<typeof mapStateToProps>;

export interface ProductImageCarouselStyles {
    carouselWrapper?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    image?: LazyImageProps;
    selectedImage?: LazyImageProps;
    verticalPrevArrowGridItem?: GridItemProps;
    verticalCarouselGridItem?: GridItemProps;
    verticalNextArrowGridItem?: GridItemProps;
}

const getImageCss = (selected = false) => css`
    width: 100%;
    height: 100%;
    max-width: 64px;
    max-height: 64px;
    border: 1px solid ${selected ? getColor("common.ui7") : getColor("common.border")};
    border-radius: 1px;
    cursor: pointer;
    justify-content: flex-start;
    ${selected ? "opacity: 1" : "opacity: 0.5"};
    img {
        max-width: 64px;
        max-height: 64px;
        height: inherit;
        padding: 3px;
    }
`;

export const productImageCarouselStyles: ProductImageCarouselStyles = {
    carouselWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            width: 100%;
            margin: 6px 0 16px;
            ${xScreen("lte", "sm")} {
                margin-bottom: 22px;
            }
        `,
    },
    carouselSlide: {
        css: css`
            flex: 0 0 25%;
            padding: 0;
            position: relative;
            margin: 12px 0 0 0;

            ${xScreen("lte", "sm")} {
                margin: 12px 20px 0 0;
                flex: 0 0 auto;
            }
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            text-align: center;
            position: relative;
            padding: 0;
            display: flex;
            height: 100%;
        `,
    },
    image: {
        css: getImageCss(),
    },
    selectedImage: {
        css: getImageCss(true),
    },
    verticalPrevArrowGridItem: {
        width: 12,
        align: "middle",
        css: css`
            justify-content: center;
        `,
    },
    verticalCarouselGridItem: { width: 12 },
    verticalNextArrowGridItem: {
        width: 12,
        align: "middle",
        css: css`
            justify-content: center;
        `,
    },
};

// export const CarouselSlidesContainer = styled.div<
//     InjectableCss & { isVertical: boolean; numberOfVisibleThumbnails: number }
// >`
//     display: flex;
//     flex-direction: ${props => (props.isVertical ? "column" : "row")};
//     align-items: ${props => (props.isVertical ? "center" : "initial")};
//     ${props => (props.isVertical ? `height: ${props.numberOfVisibleThumbnails * 74}px;` : "")}
//     ${injectCss};
// `;

// export const CarouselSlide = styled.div<InjectableCss & { numberOfVisibleThumbnails: number }>`
//     flex: 0 0 ${props => 100 / props.numberOfVisibleThumbnails}%;
//     padding: 0;
//     position: relative;
//     ${injectCss};
// `;

const ProductImageCarousel = ({
    productSettings,
    productContext,
    scrollingBehavior,
    numberOfVisibleThumbnails = 6,
    selectedIndex,
    selectedVideoIndex,
    onSelectImage,
    onSelectVideo,
    extendedStyles,
    customSettings,
    videos,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productImageCarouselStyles, extendedStyles));
    const { product } = productContext;
    const [selectedImageIndex, setSelectedImageIndex] = useState(selectedIndex);

    // XNG-Changes: upgrade adds uncompatable changes to the embla carousel, which we removed in our custom component.
    // const isVertical = scrollingBehavior === "vertical";
    // const [emblaRef, embla] = useEmblaCarousel({
    //     axis: isVertical ? "y" : "x",
    // });

    useEffect(() => {
        setSelectedImageIndex(selectedIndex);
    }, [selectedIndex]);

    const imageClickHandler = (index: number) => {
        setSelectedImageIndex(index);
        onSelectImage?.(index);
    };

    const videoClickHandler = (index: number) => {
        onSelectVideo?.(index);
    };

    const getProductImageThumbPath = (productImage: ImageModel) => {
        if (productImage.imageType === "Static" || productImage.imageType === "Image") {
            return productImage.smallImagePath || productImage.mediumImagePath;
        }
        if (productImage.imageType === "360" && productSettings.imageProvider === "SIRV") {
            return productImage.mediumImagePath + (productImage.mediumImagePath.indexOf("?") > 0 ? "&thumb" : "?thumb");
        }
        // XNG-Changes: render not found for the other types
        return customSettings.notFoundSmallImagePath || customSettings.notFoundMediumImagePath || "";
    };

    if (!product || !product.images || product.images?.length === 0) {
        return null;
    }

    // xng-changes: removing embla carousel, arrows & minimize elements
    return (
        <StyledWrapper {...styles.carouselWrapper}>
            {product.images?.map((productImage, index) => (
                <StyledWrapper {...styles.carouselSlide} key={productImage.id}>
                    <StyledWrapper {...styles.carouselSlideInner}>
                        <Clickable onClick={() => imageClickHandler(index)}>
                            <LazyImage
                                {...(index === selectedImageIndex ? styles.selectedImage : styles.image)}
                                src={getProductImageThumbPath(productImage)}
                                altText={productImage.imageAltText}
                                transformations={{ width: 62 }}
                                height="62"
                            />
                        </Clickable>
                    </StyledWrapper>
                </StyledWrapper>
            ))}
            {/* XNG-Changes add video thumbnails to image carousel */}
            {videos?.map((productVideo: VideoDocumentModel, index: number) => (
                <StyledWrapper {...styles.carouselSlide} key={`${productVideo.id}_${index}`}>
                    <StyledWrapper {...styles.carouselSlideInner}>
                        <Clickable onClick={() => videoClickHandler(index)}>
                            <LazyImage
                                {...(index === selectedVideoIndex ? styles.selectedImage : styles.image)}
                                src={productVideo.thumbnail}
                                altText={productVideo.filename}
                                transformations={{ width: 62 }}
                                height="62"
                            />
                        </Clickable>
                    </StyledWrapper>
                </StyledWrapper>
            ))}
        </StyledWrapper>
    );
};

export default withProductContext(connect(mapStateToProps)(ProductImageCarousel));
